<script setup>
  import { computed } from "vue";
  import { ChevronUp, ChevronDown } from "lucide-vue-next";
  import { Input } from "@/components/ui/input";
  import { cn } from "@/lib/utils";

  // Disable automatic class/attr inheritance so the consumer's `class` lands on
  // the inner Input (the visual element), not on the wrapper <div>.
  defineOptions({ inheritAttrs: false });

  const props = defineProps({
    min: { type: Number, default: undefined },
    max: { type: Number, default: undefined },
    step: { type: [Number, String], default: "any" },
    placeholder: { type: String, default: undefined },
    disabled: { type: Boolean, default: false },
    steppers: { type: Boolean, default: true },
    class: { type: [String, Array, Object], default: undefined },
  });

  // v-model — coerce string/empty/NaN coming from the native input into Number|null.
  // Skip prop type validation (null is a valid model value alongside Number).
  const model = defineModel({
    default: null,
    set(v) {
      if (v === "" || v == null) return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    },
  });

  // step="any" allows arbitrary decimals at the validation level; the +/- buttons
  // need a concrete delta — fall back to 1 in that case.
  const stepValue = computed(() => (typeof props.step === "number" ? props.step : 1));

  const atMin = computed(() => props.min != null && (model.value ?? props.min) <= props.min);
  const atMax = computed(() => props.max != null && (model.value ?? props.max) >= props.max);

  const adjust = (delta) => {
    if (props.disabled) return;
    const current = model.value ?? 0;
    let next = current + delta * stepValue.value;
    if (props.min != null) next = Math.max(props.min, next);
    if (props.max != null) next = Math.min(props.max, next);
    model.value = next;
  };
</script>

<template>
  <div class="relative inline-flex w-full items-stretch">
    <Input
      v-model="model"
      v-bind="$attrs"
      type="number"
      :min="min"
      :max="max"
      :step="step"
      inputmode="decimal"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="cn('w-full', steppers && 'pr-7', props.class)"
      @wheel.prevent />
    <div
      v-if="steppers"
      class="pointer-events-none absolute inset-y-1 right-1 flex w-5 flex-col">
      <button
        type="button"
        tabindex="-1"
        :disabled="disabled || atMax"
        class="pointer-events-auto flex flex-1 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent"
        @click="adjust(1)">
        <ChevronUp class="h-3 w-3" />
      </button>
      <button
        type="button"
        tabindex="-1"
        :disabled="disabled || atMin"
        class="pointer-events-auto flex flex-1 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent"
        @click="adjust(-1)">
        <ChevronDown class="h-3 w-3" />
      </button>
    </div>
  </div>
</template>

<style scoped>
  /* Hide native spin buttons — custom controls render above. */
  :deep(input[type="number"]) {
    -moz-appearance: textfield;
  }
  :deep(input[type="number"]::-webkit-outer-spin-button),
  :deep(input[type="number"]::-webkit-inner-spin-button) {
    -webkit-appearance: none;
    margin: 0;
  }
</style>
